import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header';
import HotWorkAssessmentTable from './HotWorkAssessmentTable';
import ComponentHeader from '../../../components/ComponentHeader';
import { Plus, X, CheckCircle, AlertCircle } from 'lucide-react';
import AddHotWorkApprovalModal from './NewHVModal';
import { getHotWorkApproval, deleteHotWorkApproval, getHotWorkApprovalById } from '../../../services/hotWorkApprovalService';
import Pagination from '../../../components/Pagination/Pagination';
import { formatDate } from '../../../utils/date';

interface HotWork {
  id: number;
  refNo: string;
  createdDate: string;
  workBy: string;
  location: string;
  status: string;
  statusColor: string;
  jobNumber: string;
  natureOfJob: string;
  personName: string;
}

const HotWorkApproval = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<HotWork[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [hotWorkModalFlag, setHotWorkModalFlag] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<HotWork | undefined>();
  const [editFullData, setEditFullData] = useState<any>(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [tableLoading, setTableLoading] = useState(false);

  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');
  const role = useSelector((state: any) => state.user?.role);
  const isResponsiblePersonHwa = role === 'responsible_person_hwa';
  const isTechnician = role === 'technician';

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchData = async (page = 1, search = '') => {
    //  CHANGED: added search param
    try {
      if (initialLoad) {
        setLoading(true);
      }
      const response = await getHotWorkApproval(page, search);

      const items = response?.data || [];
      // setCurrentPage(response?.meta?.current_page || 1);
      if (response?.meta?.current_page !== currentPage) {
        setCurrentPage(response?.meta?.current_page || 1);
      }
      setTotalPages(response?.meta?.last_page || 1);

      const mappedData = items.map((item: any) => {
        const formattedDate = formatDate(item.created_at, '');

        let statusColor = 'text-gray-600';
        if (item.status === 'Assessment Pending') statusColor = 'text-orange-500';
        if (item.status?.includes('Clearance Done')) statusColor = 'text-green-600';

        return {
          id: item.id,
          refNo: item.reference_number || `#HWA${String(item.id).padStart(9, '0')}`,
          createdDate: formattedDate,
          workBy: item.work_by || '-',
          location: item.location || '-',
          status: item.status || 'Assessment Pending',
          statusColor,
          jobNumber: item.job_number || '-',
          natureOfJob: item.nature_of_job || '-',
          personName: item.person_name || '-',
        };
      });

      setData(mappedData);
    } catch (error) {
      showSuccessMessage('Error', 'Failed to fetch hot work approvals. Please try again.');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const delay = searchQuery ? 150 : 0;

    debounceRef.current = setTimeout(() => {
      fetchData(currentPage, searchQuery);
    }, delay);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [currentPage, searchQuery]);
  // Handle URL query params for editing
  useEffect(() => {
    const editIdFromUrl = searchParams.get('edit');
    if (isTechnician) {
      if (editIdFromUrl) setSearchParams({});
      return;
    }
    if (editIdFromUrl) {
      const id = parseInt(editIdFromUrl);
      handleEditFromUrl(id);
    }
  }, [searchParams, isTechnician, setSearchParams]);

  const loadEditData = async (id: number) => {
    try {
      setTableLoading(true);
      const response = await getHotWorkApprovalById(id);
      const fullData = response?.data || response;

      setEditFullData(fullData);
      setEditId(id);

      const mappedData: HotWork = {
        id: fullData.id,
        refNo: fullData.reference_number || `#HWA${String(fullData.id).padStart(9, '0')}`,
        createdDate: formatDate(fullData.created_at, ''),
        workBy: fullData.work_by || '-',
        location: fullData.location || '-',
        status: fullData.status || 'Assessment Pending',
        statusColor: fullData.status?.includes('Clearance Done') ? 'text-green-600' : 'text-orange-500',
        jobNumber: fullData.job_number || '-',
        natureOfJob: fullData.nature_of_job || '-',
        personName: fullData.person_name || '-',
      };

      setSelectedItem(mappedData);
      setHotWorkModalFlag(true);
    } catch (error) {
      showSuccessMessage('Error', 'Failed to load hot work approval for editing.');
    } finally {
      setTableLoading(false);
    }
  };

  const handleEdit = async (item: HotWork) => {
    if (isTechnician) return;
    await loadEditData(item.id);
  };

  const handleEditFromUrl = async (id: number) => {
    await loadEditData(id);
  };

  const handleDelete = (item: HotWork) => {
    if (isTechnician) return;
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const showSuccessMessage = (title: string, message: string) => {
    setSuccessMessage({ title, message });
    setSuccessModal(true);
  };

  // ADDED: receives debounced query from ComponentHeader
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const isError = successMessage.title === 'Error';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <Header title={isTechnician ? 'Hot Work Approval' : 'New Hot Work Approval'} />

      <div className="flex-1 overflow-auto">
        <div className="flex justify-between items-center mb-4">
          {isResponsiblePersonHwa && (
            <button
              onClick={() => {
                setEditId(null);
                setSelectedItem(undefined);
                setHotWorkModalFlag(true);
              }}
              className="flex items-center gap-2 bg-[#e84545] text-white ml-6 px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 mt-6"
            >
              <Plus size={16} /> New Hot Work Approval
            </button>
          )}
          <div className="ml-auto">
            <ComponentHeader onSearch={handleSearch} />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* NEW — pass tableLoading to show loader inside table during add/edit */}
            <HotWorkAssessmentTable
              data={data}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isLoading={tableLoading}
              readOnly={isTechnician}
            />

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </>
        )}
      </div>

      {hotWorkModalFlag && !isTechnician && (
        <AddHotWorkApprovalModal
          editId={editId}
          initialData={selectedItem}
          editFullData={editFullData}
          onClose={() => {
            setHotWorkModalFlag(false);
            setEditId(null);
            setSelectedItem(undefined);
            setEditFullData(null);
            setSearchParams({});
          }}
          onSuccess={async (message) => {
            setHotWorkModalFlag(false);
            setTableLoading(true);
            showSuccessMessage(message.title, message.message);
            await fetchData(currentPage);
            setTableLoading(false);
            setEditId(null);
            setSelectedItem(undefined);
            setEditFullData(null);
            setSearchParams({});
          }}
        />
      )}

      {isDeleteOpen && !isTechnician && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full relative">
            <button onClick={() => setIsDeleteOpen(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
            <h3 className="text-lg font-semibold mb-4">Are you sure you want to delete this Hot Work Approval?</h3>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteOpen(false)}
                disabled={deleteLoading}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    if (selectedItem) {
                      setDeleteLoading(true);
                      await deleteHotWorkApproval(selectedItem.id);
                      setIsDeleteOpen(false);
                      setDeleteLoading(false);
                      setTableLoading(true);
                      showSuccessMessage('Hot Work Approval Deleted', 'The Hot Work Approval has been successfully deleted.');
                      await fetchData(currentPage);
                      setTableLoading(false);
                    }
                  } catch (error) {
                    setDeleteLoading(false);
                    setTableLoading(false);
                    showSuccessMessage('Error', 'Failed to delete hot work approval. Please try again.');
                  }
                }}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md flex items-center gap-2 disabled:opacity-70"
              >
                {deleteLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {successModal && (
        <div className="fixed bottom-6 right-6 z-[60]">
          <div className={`rounded-lg shadow-lg p-4 min-w-[380px] ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
            <div className="flex items-start gap-3">
              {isError ? <AlertCircle className="w-6 h-6 text-white" /> : <CheckCircle className="w-6 h-6 text-white" />}
              <div className="flex-1">
                <h4 className="text-white font-semibold">{successMessage.title}</h4>
                <p className="text-white text-sm">{successMessage.message}</p>
              </div>
              <button onClick={() => setSuccessModal(false)}>
                <X className="text-white" size={18} />
              </button>
            </div>
            <button
              onClick={() => setSuccessModal(false)}
              className={`mt-3 w-full bg-white rounded-md py-2 font-medium ${isError ? 'text-red-600' : 'text-green-600'}`}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotWorkApproval;
